import { useT } from '@transifex/react'
import { useCallback } from 'react'
import { EVENT } from '../../../../../constants'
import {
  FulfilmentType,
  isDefinedFulfilmentType,
} from '../../../../../models/fulfilment'
import { useSaleInventoryData } from '../../../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../../../state/hooks'
import {
  selectCurrentSale,
  selectIsEcomOrder,
} from '../../../../../state/slices/currentSale/currentSaleSlice'
import {
  openFulfilmentLineItemPopover,
  setLineItemFulfilmentNoteError,
} from '../../../../../state/slices/uiStateSlice'
import { getFeatureFromCache } from '../../../../../util/feature-cache'
import { FEATURE_ALLOW_EDIT_UNFULFILLED_SALES } from '../../../../../constants/wr-features'
import { computeFulfilmentTypeRestrictions } from '../../../../../functions/sale'
import { NoteError } from '../../../../shared/NoteInput'
import { useAutoPopulateFulfillmentAddresses } from '../hook/useAutoPopulateFulfillmentAddresses'
import { useSaleFulfilment } from '../hook/useSaleFulfilment'
import { LineItem } from '../../../../../models/line-item'
import { useCrossLocationFulfilmentGate } from '../OutletSelect'
import { useSpecialOrderProduct } from '../../../../special-order-product/useSpecialOrderProduct'

export type LineItemDisplayFulfilmentProps = {
  lineItem: LineItem
  onSelectFulfilmentType?: (
    type: FulfilmentType,
    fulfilmentOutletId?: string
  ) => void
}

export const useLineItemDisplayFulfilmentModel = ({
  lineItem: defaultLineItem,
  onSelectFulfilmentType,
}: LineItemDisplayFulfilmentProps) => {
  const t = useT()
  const saleInventoryData = useSaleInventoryData()
  const dispatch = useAppDispatch()

  const {
    currentLineItem,
    fulfilmentDetail,
    setFulfilmentType,
    setFulfilmentNote,
    isDisabled,
    isNoteDisabled,
    isFulfilmentOutletDisabled,
    setFulfilmentOutletId,
  } = useSaleFulfilment(defaultLineItem.localId)
  const isSpecialOrderProduct = useSpecialOrderProduct(currentLineItem)
  // Auto populate fulfillment addresses based on customer addresses
  useAutoPopulateFulfillmentAddresses(fulfilmentDetail)

  const isEcomOrder = useAppSelector(selectIsEcomOrder)
  const currentSale = useAppSelector(selectCurrentSale)

  const handleNoteErrorsChange = (errors: NoteError[]) => {
    if (errors.length === 0) {
      setLineItemFulfilmentNoteError({
        id: currentLineItem.localId,
        error: null,
      })

      return
    }

    setLineItemFulfilmentNoteError({
      id: currentLineItem.localId,
      error: errors[0],
    })
  }

  const handleTypeChange = (type: FulfilmentType) => {
    setFulfilmentType(type, fulfilmentDetail?.note || '')
    if (onSelectFulfilmentType) {
      onSelectFulfilmentType(type)
    }
    if (!isDefinedFulfilmentType(type)) {
      saleInventoryData.enableInventoryValidation(currentLineItem)
      if (
        Number(currentLineItem.quantity) > 0 &&
        saleInventoryData.isInventoryTracked(currentLineItem) &&
        !saleInventoryData.hasInventory(currentLineItem)
      ) {
        dispatch(openFulfilmentLineItemPopover(currentLineItem.localId))
      }
    } else {
      saleInventoryData.disableInventoryValidation(currentLineItem)
    }
    saleInventoryData.emit(EVENT.LINE_ITEM_UPDATE_VALIDATION)
  }

  const { canUseCrossLocationFulfilment } = useCrossLocationFulfilmentGate()
  const shouldShowOutletSelect =
    canUseCrossLocationFulfilment &&
    isDefinedFulfilmentType(currentLineItem?.fulfilmentType)

  const handleSelectedOutletIdChange = useCallback(
    (outletId?: string) => {
      if (!outletId || currentLineItem?.fulfilmentOutletId) {
        return
      }

      setFulfilmentOutletId(outletId)
    },
    [currentLineItem?.fulfilmentOutletId, setFulfilmentOutletId]
  )

  const shouldShowFulfilmentAddress =
    currentLineItem?.fulfilmentType === FulfilmentType.DELIVERY && !isEcomOrder

  const ffSpecialOrder = getFeatureFromCache('inventory_special_orders')
  const shouldShowSpecialOrderMessage =
    ffSpecialOrder &&
    isDefinedFulfilmentType(currentLineItem?.fulfilmentType) &&
    Boolean(currentLineItem && isSpecialOrderProduct(currentLineItem))

  // For new items added while continuing a fulfilment sale, restrict the
  // selectable fulfilment types to the sale's existing type and None (Take Now).
  // Switching a pickup sale to delivery (or vice versa) is never allowed.
  // Layby and on-account sales are excluded: new items there keep the sale's
  // type and cannot be set to None (Take Now).
  const saleFulfilmentType = fulfilmentDetail?.type
  const allowedFulfilmentTypes = getFeatureFromCache(
    FEATURE_ALLOW_EDIT_UNFULFILLED_SALES
  )
    ? computeFulfilmentTypeRestrictions(
        currentLineItem,
        currentSale,
        saleFulfilmentType
      )
    : undefined

  return {
    currentLineItem,
    defaultLineItem,
    fulfilmentDetail,
    handleNoteErrorsChange,
    handleTypeChange,
    isDisabled,
    isNoteDisabled,
    allowedFulfilmentTypes,
    handleSelectedOutletIdChange,
    isFulfilmentOutletDisabled,
    setFulfilmentNote,
    setFulfilmentOutletId,
    shouldShowFulfilmentAddress,
    shouldShowOutletSelect,
    fulfilmentMessage: t(
      'You can select one method per sale. To set another method, create another sale.',
      {
        _context: 'Note explaining how fulfillment methods work',
      }
    ),
    outOfStockMessage: shouldShowSpecialOrderMessage
      ? t(
          'This product is out of stock. If incoming stock is insufficient, it will be available for special order and ready to be fulfilled upon restock.',
          {
            _context: 'Special order product info message',
          }
        )
      : undefined,
  }
}
